import { useMutation, useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogSectionHeader } from '../CatalogSectionHeader';
import {
  CATEGORIES_QUERY,
  CREATE_CATEGORY_MUTATION,
  SET_CATEGORY_STATUS_MUTATION,
  UPDATE_CATEGORY_MUTATION,
} from './categories.gql';
import { CategoryFormDialog, type CategoryFormValues } from './CategoryFormDialog';

import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { CATEGORY_KIND_FROM_GQL, CATEGORY_KIND_TO_GQL } from '@/shared/graphql/enums';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface Category {
  id: string;
  name: string;
  kind: 'product' | 'service' | 'mixed';
  parent_id: string | null;
  status: 'active' | 'inactive';
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface CategoriesResponse {
  categories: {
    items: Category[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

export function CategoriesPage(): JSX.Element {
  const { t } = useTranslation();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_manage = user?.permissions.includes('admin.catalog.manage') ?? false;

  const [kind_filter, setKindFilter] = useState<'product' | 'service' | 'mixed' | 'ALL'>('ALL');
  const [editing, setEditing] = useState<Category | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, loading, refetch } = useQuery<CategoriesResponse>(CATEGORIES_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
      search: list.search || null,
      status_filter: list.status_filter,
      kind: kind_filter === 'ALL' ? null : CATEGORY_KIND_TO_GQL[kind_filter],
    },
  });

  const [createCategory, { loading: creating_loading }] = useMutation(CREATE_CATEGORY_MUTATION);
  const [updateCategory, { loading: updating_loading }] = useMutation(UPDATE_CATEGORY_MUTATION);
  const [setStatus] = useMutation(SET_CATEGORY_STATUS_MUTATION);
  const rows = (data?.categories.items ?? []).map((row) => ({
    ...row,
    kind: CATEGORY_KIND_FROM_GQL[row.kind as keyof typeof CATEGORY_KIND_FROM_GQL] ?? 'mixed',
  }));

  const onSubmitCreateCategory = async (values: CategoryFormValues): Promise<void> => {
    try {
      const result = await createCategory({
        variables: {
          input: {
            name: values.name,
            kind: CATEGORY_KIND_TO_GQL[values.kind],
            sort_order: values.sort_order ?? 0,
          },
        },
      });
      assertMutationSucceeded(result, 'create_category_failed');
      setCreating(false);
      showToast({
        tone: 'success',
        title: t('alerts.create_success_title'),
        body: t('alerts.create_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.create_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onSubmitUpdateCategory = async (values: CategoryFormValues): Promise<void> => {
    if (!editing) return;
    try {
      const result = await updateCategory({
        variables: {
          id: editing.id,
          input: {
            name: values.name,
            kind: CATEGORY_KIND_TO_GQL[values.kind],
            sort_order: values.sort_order ?? 0,
          },
        },
      });
      assertMutationSucceeded(result, 'update_category_failed');
      setEditing(null);
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onClickToggleStatus = async (row: Category): Promise<void> => {
    try {
      const result = await setStatus({
        variables: { id: row.id, status: row.status === 'active' ? 'inactive' : 'active' },
      });
      assertMutationSucceeded(result, 'update_category_status_failed');
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const columns: CrudColumn<Category>[] = [
    { key: 'name', header_key: 'catalog.categories.name', render: (r) => r.name },
    {
      key: 'kind',
      header_key: 'catalog.categories.kind',
      width: 'w-32',
      render: (r) => <Badge variant="outline">{t(`catalog.categories.kind_${r.kind}`)}</Badge>,
    },
    {
      key: 'sort_order',
      header_key: 'catalog.categories.sort_order',
      align: 'right',
      width: 'w-24',
      render: (r) => r.sort_order,
    },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-32',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-48',
      render: (r) =>
        can_manage ? (
          <div className="flex justify-end gap-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(r)}>
              {t('common.edit')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onClickToggleStatus(r);
              }}
            >
              {r.status === 'active' ? t('common.deactivate') : t('common.activate')}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <CatalogSectionHeader
        title={t('catalog.categories.title')}
        subtitle={t('catalog.categories.subtitle')}
      />

      <CrudTable<Category>
        rows={rows}
        columns={columns}
        loading={loading}
        page={data?.categories.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="catalog.categories.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            filters={
              <select
                value={kind_filter}
                onChange={(e) => setKindFilter(e.target.value as typeof kind_filter)}
                className="border-input bg-background focus:ring-ring h-10 rounded-md border px-3 text-sm shadow-sm focus:outline-none focus:ring-2"
              >
                <option value="ALL">{t('catalog.categories.kind_all')}</option>
                <option value="product">{t('catalog.categories.kind_product')}</option>
                <option value="service">{t('catalog.categories.kind_service')}</option>
                <option value="mixed">{t('catalog.categories.kind_mixed')}</option>
              </select>
            }
            primary_action={
              can_manage ? (
                <Button type="button" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('catalog.categories.create')}</span>
                </Button>
              ) : undefined
            }
          />
        }
      />

      {creating ? (
        <CategoryFormDialog
          title={t('catalog.categories.create')}
          submitting={creating_loading}
          onSubmit={onSubmitCreateCategory}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      {editing ? (
        <CategoryFormDialog
          title={t('catalog.categories.edit')}
          initial_values={{
            name: editing.name,
            kind: editing.kind,
            sort_order: editing.sort_order,
          }}
          submitting={updating_loading}
          onSubmit={onSubmitUpdateCategory}
          onCancel={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
